import { Component, OnInit, EventEmitter, Output, ViewChild, ElementRef, } from '@angular/core';
import { environment } from 'src/environments/environment';
import { MatDialog } from '@angular/material/dialog';
import * as L from 'leaflet';
import { UnitSelectionDialogComponent } from './unit-selection-dialog/unit-selection-dialog.component';
import { MapStateCheckService } from '../../services/map-state-check/map-state-check.service';
import { min } from 'rxjs';

declare var RiskscapeMap: any;
declare var RiskscapeSearch: any;

@Component({
  selector: 'app-map-search',
  templateUrl: './map-search.component.html',
  styleUrls: ['./map-search.component.scss']
})
export class MapSearchComponent implements OnInit {

  @Output() selectedProperty: EventEmitter<any> = new EventEmitter();
  @Output() selectedUnitNumber: EventEmitter<any> = new EventEmitter();
  @Output() scriptLoadStatus: EventEmitter<string> = new EventEmitter();

  private map: any;
  private search: any;
  private apiKey: string = environment.riskscapeApiKey;
  private coordinates: [number, number] = [-25.7735, 28.2308];
  private defaultUnitNumber: string = '00000';

  constructor(private dialog: MatDialog, private _mapStateCheck: MapStateCheckService) { }

  ngOnInit(): void {
    this.loadStyles();

    this.loadScripts().then(() => {
      this.initMapAndSearch();

      this._mapStateCheck.mapState$.subscribe((state: boolean) => {
        if (state) this.reloadMap();
      });
    })


    this._mapStateCheck.mapState$.subscribe((state: boolean) => {
      if (state) {
        this.reloadMap();
      }
    });
  }

  private loadStyles(): void {
    const stylesheets = [
      'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css',
      'https://unpkg.com/riskscape-search@0.8.0/dist/search.css'
    ];

    stylesheets.forEach(href => {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = href;
      document.head.appendChild(link);
    });
  }

  private loadScripts(): Promise<void[]> {
    const scripts = [
      'https://unpkg.com/riskscape-search@0.8.0/dist/search.umd.js',
      'https://unpkg.com/riskscape-map@0.8.0/dist/map.umd.js'
    ];

    return Promise.all(scripts.map(src => this.loadScript(src)));
  }

  private loadScript(src: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = src;
      script.onload = () => resolve();
      script.onerror = () => reject();
      document.body.appendChild(script);
    });
  }

  private initMapAndSearch(): void {
    const savedMap = JSON.parse(localStorage.getItem('map') || 'null');

    const defaultOptions = {
      center: this.coordinates,
      zoom: 18,
      minZoom: 2,
      maxZoom: 20
    };

    const mapOptions = savedMap
      ? { ...defaultOptions, center: savedMap.center, zoom: savedMap.zoom }
      : defaultOptions;

    this.map = new RiskscapeMap({
      key: this.apiKey,
      element: 'map',
      options: {
        center: mapOptions.center,
        zoom: mapOptions.zoom,
        minZoom: 2,
        maxZoom: 20, 
        layers: [
          L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
            maxZoom: 20 
          })
        ]
      }
    });

    this.search = new RiskscapeSearch({
      key: this.apiKey,
      element: 'search'
    });

    this.map.bindSearch(this.search);

    this.map.on('zoomend moveend', () => {
      const data = {
        zoom: this.map.getZoom(),
        center: this.map.getCenter()
      };
      localStorage.setItem('map', JSON.stringify(data));
    });

    this.map.on('click', (event: any) => {
      this.map.selectFeature('property_scheme', event.latlng);
    });

    this.map.on('feature:selected', async ({ feature }: any) => {
      const hasUnits = feature?.has_sectional_units;
      if (hasUnits) {
        let units = feature.type === 'scheme'
          ? await this.map.getSchemeUnits(feature.scheme_id)
          : await this.map.getPropertyUnits(feature.property_key);

        if (feature.type === 'property') {
          units = units.map((unit: any) => ({
            unit_number: unit,
            property_key: feature.property_key
          }));
        }

        this.openUnitSelectionDialog(units);
        return;
      }

      if (!feature?.property_key) return;

      this.showConfirmProperty(feature.property_key, feature.unit_number ?? this.defaultUnitNumber);
    });
  }


  private openUnitSelectionDialog(units: any[]): void {
    const dialogRef = this.dialog.open(UnitSelectionDialogComponent, {
      width: '20vw',
      data: { units }
    });

    dialogRef.afterClosed().subscribe((selectedUnit: any) => {
      if (selectedUnit) {
        this.showConfirmProperty(selectedUnit.property_key, selectedUnit.unit_number);
      }
    });
  }

  private showConfirmProperty(property_key: string, unit_number: string): void {
    this.map.getProperty(property_key, unit_number).then((data: any) => {
      this.selectedProperty.emit(data);
    });
  }

  reloadMap(): void {
    setTimeout(() => {
      if (this.map && typeof this.map.invalidateSize === 'function') {
        this.map.invalidateSize();
      }
    }, 200);
  }


}
